import { ETaskStatus } from '@constants';

import { ICourse } from '../course';
import { IFile } from '../file';
import { IQuiz } from '../quiz';
import { IBasicUser } from '../user';

export type ITask = {
  _id: string;
  title: string;
  description: string;
  creator: IBasicUser;
  assignee: IBasicUser;
  course: ICourse;
  statusInfo: ITaskStatusInfo;
  deadline: Date;
  createdAt: Date;
  resource?: {
    file: IFile;
    quiz?: IQuiz;
  };
  validation?: ITaskValidation;
};

export type ICreateTask = {
  title: string;
  description: string;
  creator: IBasicUser;
  assignee: IBasicUser;
  course: string;
  statusInfo?: ITaskStatusInfo;
  deadline: Date;
  resource?: {
    file: IFile;
    quiz?: IQuiz;
  };
  validation?: ITaskValidation;
};

export type IUpdateTask = {
  _id: string;
  title: string;
  description: string;
  creator: IBasicUser;
  assignee: IBasicUser;
  course: string;
  statusInfo?: ITaskStatusInfo;
  deadline: Date;
  resource?: {
    file: IFile;
    quiz?: IQuiz;
  };
  validation?: ITaskValidation;
};

export type ITaskStatusInfo = {
  currentStatus: ETaskStatus;
  logs: ITaskStatusLog[];
};

export type ITaskStatusLog = {
  status: ETaskStatus;
  date: Date;
  changedBy?: IBasicUser;
};

export type ITaskValidation = {
  checks?: ITaskValidationChecks;
  comments: string;
};

export type ITaskValidationChecks = {
  technical: ITechnicalChecks;
  content: IContentChecks;
  performance: IPerformanceChecks;
};

export type ITechnicalChecks = {
  imageAndSound: boolean;
  sceneTransitions: boolean;
  intro: boolean;
};

export type IContentChecks = {
  shortAndClear: boolean;
  classStructure: boolean;
  correctPresentation: boolean;
  practiceExamples: boolean;
  visualResources: boolean;
};

export type IPerformanceChecks = {
  introduction: boolean;
  voiceTone: boolean;
  notReading: boolean;
  useTablet: boolean;
  goodPosture: boolean;
  rhetoricalQuestions: boolean;
};

export enum EContentChecks {
  SHORT_AND_CLEAR = 'shortAndClear',
  CLASS_STRUCTURE = 'classStructure',
  CORRECT_PRESENTATION = 'correctPresentation',
  PRACTICE_EXAMPLES = 'practiceExamples',
  VISUAL_RESOURCES = 'visualResources'
}

export const CONTENT_CHECKS = [
  EContentChecks.SHORT_AND_CLEAR,
  EContentChecks.CLASS_STRUCTURE,
  EContentChecks.CORRECT_PRESENTATION,
  EContentChecks.PRACTICE_EXAMPLES,
  EContentChecks.VISUAL_RESOURCES
];

export enum EPerformanceChecks {
  INTRODUCTION = 'introduction',
  VOICE_TONE = 'voiceTone',
  NOT_READING = 'notReading',
  USE_TABLET = 'useTablet',
  GOOD_POSTURE = 'goodPosture',
  RHETORICAL_QUESTIONS = 'rhetoricalQuestions'
}

export const PERFORMANCE_CHECKS = [
  EPerformanceChecks.INTRODUCTION,
  EPerformanceChecks.VOICE_TONE,
  EPerformanceChecks.NOT_READING,
  EPerformanceChecks.USE_TABLET,
  EPerformanceChecks.GOOD_POSTURE,
  EPerformanceChecks.RHETORICAL_QUESTIONS
];

export enum ETechnicalChecks {
  IMAGE_AND_SOUND = 'imageAndSound',
  SCENE_TRANSITIONS = 'sceneTransitions',
  INTRO = 'intro'
}

export const TECHNICAL_CHECKS = [
  ETechnicalChecks.IMAGE_AND_SOUND,
  ETechnicalChecks.SCENE_TRANSITIONS,
  ETechnicalChecks.INTRO
];

export type ITaskFilters = {
  courseId?: string;
  assigneeId?: string;
  status: ETaskStatus;
};

export interface IGanttTask {
  TaskID: string;
  TaskName: string;
  StartDate: Date;
  EndDate: Date;
  Progress: number;
  resources?: string[];
  subtasks?: IGanttTask[];
}

export interface IGanttResource {
  resourceId: string;
  resourceName: string;
}

export interface IGanttTaskBarProperties {
  index: number;
  ganttProperties: IGanttTaskProperties;
  taskData: TaskData;
  TaskID: string;
  TaskName: string;
  StartDate: Date;
  EndDate: Date;
  Duration: number;
  Progress: number;
  resources: string | null;
  parentItem: ParentItem | null;
  parentUniqueID: null | string;
  level: number;
  uniqueID: string;
  childRecords: IGanttTaskBarProperties[];
  hasChildRecords: boolean;
  expanded: boolean;
}

interface IGanttTaskProperties {
  taskId: string;
  taskName: string;
  isAutoSchedule: boolean;
  resourceInfo: ResourceElement[] | null;
  isMilestone: boolean;
  indicators: null;
  resourceNames?: string;
  durationUnit: string;
  workUnit: string;
  taskType: string;
  startDate: Date;
  duration: number;
  endDate: Date;
  baselineStartDate: null;
  baselineEndDate: null;
  progress: number;
  totalProgress: number;
  predecessorsName: null;
  notes: null;
  cssClass: null;
  uniqueID: string;
  work: number;
  rowUniqueID: string;
  width: number;
  left: number;
  progressWidth: number;
  baselineLeft: number;
  baselineWidth: number;
  autoStartDate?: Date;
  autoEndDate?: Date;
  autoDuration?: number;
  totalDuration?: number;
}

export interface ResourceElement {
  resourceId: string;
  resourceName: string;
  unit: number;
}

export interface ParentItem {
  uniqueID: string;
  expanded: boolean;
  level: number;
  index: number;
  taskId: string;
}

export interface TaskData {
  TaskID: string;
  TaskName: string;
  StartDate: Date;
  EndDate: Date;
  Progress: number;
  resources?: ResourceElement[];
  Duration: number;
  subtasks?: TaskData[];
}
